import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaLock, FaEye, FaEyeSlash, FaCopy, FaPlus, FaShieldAlt, FaTrash, FaUnlock, FaKey } from 'react-icons/fa';
import { useVault, useSettings, addToCollection, deleteFromCollection } from '../hooks/useFirestore';
import { encrypt, decrypt, hashMasterPassword } from '../utils/encryption';

const AUTO_LOCK_MS = 5 * 60 * 1000; // 5 minutes

const PasswordVault = () => {
    const { data: vaultItems } = useVault();
    const { settings, updateSettings } = useSettings();
    const [showPassword, setShowPassword] = useState({});
    const [masterPassword, setMasterPassword] = useState('');
    const [unlocked, setUnlocked] = useState(false);
    const [showSetup, setShowSetup] = useState(false);
    const [setupPassword, setSetupPassword] = useState('');
    const [setupConfirm, setSetupConfirm] = useState('');
    const [verifyInput, setVerifyInput] = useState('');
    const [error, setError] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [decryptedItems, setDecryptedItems] = useState({});
    const lockTimer = useRef(null);

    const hasVaultPassword = !!settings?.vaultPasswordHash;

    // Auto-lock timer
    const resetLockTimer = useCallback(() => {
        if (lockTimer.current) clearTimeout(lockTimer.current);
        lockTimer.current = setTimeout(() => {
            setUnlocked(false);
            setMasterPassword('');
            setDecryptedItems({});
            setShowPassword({});
        }, AUTO_LOCK_MS);
    }, []);

    useEffect(() => {
        if (unlocked) {
            resetLockTimer();
            const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
            events.forEach(e => window.addEventListener(e, resetLockTimer));
            return () => {
                events.forEach(e => window.removeEventListener(e, resetLockTimer));
                if (lockTimer.current) clearTimeout(lockTimer.current);
            };
        }
    }, [unlocked, resetLockTimer]);

    // Show setup if no vault password hash exists
    useEffect(() => {
        if (settings && !settings.vaultPasswordHash) {
            setShowSetup(true);
        }
    }, [settings]);

    const handleSetup = async () => {
        if (setupPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
        if (setupPassword !== setupConfirm) { setError('Passwords do not match'); return; }
        const hash = await hashMasterPassword(setupPassword);
        await updateSettings({ vaultPasswordHash: hash });
        setMasterPassword(setupPassword);
        setUnlocked(true);
        setShowSetup(false);
        setError('');
    };

    const handleVerify = async () => {
        const hash = await hashMasterPassword(verifyInput);
        if (hash === settings.vaultPasswordHash) {
            setMasterPassword(verifyInput);
            setUnlocked(true);
            setVerifyInput('');
            setError('');
        } else {
            setError('Wrong master password');
        }
    };

    // Decrypt a vault item's password and notes
    const decryptItem = async (item) => {
        if (decryptedItems[item.id]) return;
        try {
            const decryptedPassword = item.encryptedPassword ? await decrypt(item.encryptedPassword, masterPassword) : '';
            const decryptedNotes = item.encryptedNotes ? await decrypt(item.encryptedNotes, masterPassword) : '';
            setDecryptedItems(prev => ({ ...prev, [item.id]: { password: decryptedPassword, notes: decryptedNotes } }));
        } catch (e) {
            setError('Decryption failed — vault may be corrupted');
        }
    };

    const toggleShow = async (item) => {
        if (!showPassword[item.id]) {
            await decryptItem(item);
        }
        setShowPassword(prev => ({ ...prev, [item.id]: !prev[item.id] }));
    };

    const copyToClipboard = async (item) => {
        await decryptItem(item);
        const pwd = decryptedItems[item.id]?.password || '';
        if (pwd) {
            await navigator.clipboard.writeText(pwd);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this secret permanently?')) return;
        await deleteFromCollection('vault', id);
    };

    const handleAddSecret = async (data) => {
        const encryptedPassword = await encrypt(data.password, masterPassword);
        const encryptedNotes = data.notes ? await encrypt(data.notes, masterPassword) : null;
        await addToCollection('vault', {
            service: data.service,
            identity: data.identity,
            encryptedPassword,
            encryptedNotes,
        });
        setShowAddForm(false);
    };

    // Master password setup screen
    if (showSetup) {
        return (
            <div className="space-y-8">
                <VaultHeader count={0} locked />
                <div className="glass-card p-12 max-w-md mx-auto text-center space-y-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center mx-auto text-white text-2xl shadow-glow">
                        <FaKey />
                    </div>
                    <h3 className="text-lg font-bold text-frost-900">Set Master Password</h3>
                    <p className="text-sm text-frost-500">This password will encrypt all your vault secrets. Choose something strong and memorable.</p>
                    <div className="space-y-3 text-left">
                        <input type="password" placeholder="Master password (min 6 chars)" value={setupPassword} onChange={e => setSetupPassword(e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                        <PasswordStrength password={setupPassword} />
                        <input type="password" placeholder="Confirm password" value={setupConfirm} onChange={e => setSetupConfirm(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSetup()}
                            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    </div>
                    {error && <p className="text-xs text-gold-400">{error}</p>}
                    <button onClick={handleSetup} className="w-full py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold rounded-xl shadow-glow text-sm">
                        Set Master Password
                    </button>
                </div>
            </div>
        );
    }

    // Locked screen — verify master password
    if (!unlocked && hasVaultPassword) {
        return (
            <div className="space-y-8">
                <VaultHeader count={vaultItems?.length || 0} locked />
                <div className="glass-card p-12 max-w-md mx-auto text-center space-y-6">
                    <div className="w-16 h-16 bg-white/[0.03] rounded-2xl flex items-center justify-center mx-auto border border-white/[0.06]">
                        <FaLock className="text-2xl text-frost-300" />
                    </div>
                    <h3 className="text-lg font-bold text-frost-900">Vault Locked</h3>
                    <p className="text-sm text-frost-500">Enter your master password to access the vault</p>
                    <input type="password" placeholder="Master password" value={verifyInput}
                        onChange={e => { setVerifyInput(e.target.value); setError(''); }}
                        onKeyDown={e => e.key === 'Enter' && handleVerify()}
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    {error && <p className="text-xs text-gold-400">{error}</p>}
                    <button onClick={handleVerify} className="w-full py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold rounded-xl shadow-glow text-sm">
                        <FaUnlock className="inline mr-2" /> Unlock Vault
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <VaultHeader count={vaultItems?.length || 0} locked={false} onAdd={() => setShowAddForm(true)} onLock={() => { setUnlocked(false); setMasterPassword(''); setDecryptedItems({}); }} />

            <div className="glass-card overflow-hidden relative">
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent-500/5 blur-[80px] rounded-full"></div>
                <div className="p-8 border-b border-white/[0.04] flex items-center justify-between">
                    <div className="flex items-center gap-5">
                        <div className="p-4 bg-accent-500/10 rounded-xl text-accent-400 border border-accent-500/20"><FaShieldAlt className="text-xl" /></div>
                        <div>
                            <h3 className="font-bold text-lg text-frost-900 tracking-tight">{vaultItems?.length > 0 ? 'Encrypted Assets' : 'Vault Empty'}</h3>
                            <p className="text-[10px] text-frost-400 font-medium mt-1">{vaultItems?.length > 0 ? `${vaultItems.length} Secure Items` : 'No items stored'}</p>
                        </div>
                    </div>
                    <span className="px-4 py-1.5 bg-gradient-to-r from-accent-500 to-accent-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-glow">Military Grade</span>
                </div>

                {(!vaultItems || vaultItems.length === 0) ? (
                    <div className="p-14 text-center">
                        <div className="w-16 h-16 bg-white/[0.03] rounded-2xl flex items-center justify-center mx-auto mb-5 border border-white/[0.06]"><FaLock className="text-2xl text-frost-300" /></div>
                        <h4 className="font-bold text-lg text-frost-900 mb-2">Vault is empty</h4>
                        <p className="text-sm text-frost-400 font-medium max-w-md mx-auto">Add passwords, API keys, and sensitive credentials. Everything is encrypted locally.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-separate border-spacing-0">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-widest text-frost-400 font-medium">
                                    <th className="px-8 py-5 border-b border-white/[0.04]">Service</th>
                                    <th className="px-8 py-5 border-b border-white/[0.04]">Identity</th>
                                    <th className="px-8 py-5 border-b border-white/[0.04]">Secret</th>
                                    <th className="px-8 py-5 border-b border-white/[0.04] text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {vaultItems.map(item => (
                                    <VaultItem key={item.id} item={item}
                                        show={showPassword[item.id]}
                                        decrypted={decryptedItems[item.id]}
                                        onToggle={() => toggleShow(item)}
                                        onCopy={() => copyToClipboard(item)}
                                        onDelete={() => handleDelete(item.id)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="glass-card p-8 flex flex-col md:flex-row items-center gap-8 border-accent-500/10 group relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-accent-500/5 blur-3xl rounded-full"></div>
                <div className="p-5 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl text-white text-2xl shadow-glow group-hover:scale-105 transition-transform duration-500 relative z-10"><FaLock /></div>
                <div className="relative z-10">
                    <h4 className="font-bold text-frost-900 text-lg mb-2 tracking-tight">Zero-Knowledge Architecture</h4>
                    <p className="text-sm text-frost-500 leading-relaxed">Your secrets are encrypted locally before storage. All decryption keys are derived from your master password. Auto-locks after 5 minutes of inactivity.</p>
                </div>
            </div>

            {/* Add Secret Modal */}
            {showAddForm && <AddSecretModal onSave={handleAddSecret} onClose={() => setShowAddForm(false)} />}
        </div>
    );
};

const VaultHeader = ({ count, locked, onAdd, onLock }) => (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
            <p className="text-[10px] font-semibold text-accent-400 uppercase tracking-[0.2em] mb-1">Security</p>
            <h1 className="text-2xl font-bold tracking-tight text-frost-900">Password Vault</h1>
            <p className="text-frost-500 mt-0.5 text-sm">AES-256 Encrypted private storage</p>
        </div>
        <div className="flex gap-3">
            <div className="bg-white/[0.03] border border-white/[0.06] px-5 py-2.5 rounded-full flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${locked ? 'bg-gold-500' : 'bg-mint-500'} animate-pulse-soft`}></div>
                <span className="text-[10px] font-bold text-frost-500 uppercase tracking-widest">{locked ? 'Vault Locked' : 'Vault Unlocked'}</span>
            </div>
            {!locked && onLock && (
                <button onClick={onLock} className="btn-pill bg-white/[0.03] border border-white/[0.06] text-frost-400 hover:text-gold-400 flex items-center gap-2 text-xs">
                    <FaLock className="text-xs" /> Lock
                </button>
            )}
            {!locked && onAdd && (
                <button onClick={onAdd} className="btn-pill btn-glow flex items-center gap-2">
                    <FaPlus className="text-xs" /> Add Secret
                </button>
            )}
        </div>
    </div>
);

const VaultItem = ({ item, show, decrypted, onToggle, onCopy, onDelete }) => (
    <tr className="hover:bg-white/[0.02] group transition-all duration-300">
        <td className="px-8 py-6">
            <div className="flex items-center gap-5">
                <div className="w-10 h-10 bg-white/[0.03] border border-white/[0.06] rounded-xl flex items-center justify-center text-sm font-bold text-frost-400 group-hover:border-accent-500/30 group-hover:text-accent-400 transition-all">
                    {(item.service || '?')[0].toUpperCase()}
                </div>
                <span className="text-sm font-bold text-frost-900 tracking-tight">{item.service}</span>
            </div>
        </td>
        <td className="px-8 py-6 text-sm font-medium text-frost-400">{item.identity}</td>
        <td className="px-8 py-6">
            <div className="flex items-center gap-3">
                <input type={show ? "text" : "password"} readOnly value={show && decrypted ? decrypted.password : '••••••••'}
                    className="bg-transparent text-sm font-mono font-bold text-accent-300 focus:outline-none w-48 tracking-widest" />
                <button onClick={onToggle} className="text-frost-300 hover:text-accent-400 transition-all outline-none">
                    {show ? <FaEyeSlash /> : <FaEye />}
                </button>
            </div>
        </td>
        <td className="px-8 py-6 text-right">
            <div className="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-all duration-500">
                <button onClick={onCopy} className="p-2.5 text-frost-400 hover:text-accent-400 hover:bg-accent-500/10 rounded-lg transition-all"><FaCopy className="text-sm" /></button>
                <button onClick={onDelete} className="p-2.5 text-frost-400 hover:text-gold-400 hover:bg-gold-500/10 rounded-lg transition-all"><FaTrash className="text-sm" /></button>
            </div>
        </td>
    </tr>
);

const PasswordStrength = ({ password }) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    const levels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
    const colors = ['', 'bg-gold-500', 'bg-orange-400', 'bg-accent-400', 'bg-mint-400', 'bg-mint-500'];
    if (!password) return null;
    return (
        <div className="space-y-1">
            <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
                <div className={`h-full ${colors[score] || 'bg-gold-500'} rounded-full transition-all duration-500`} style={{ width: `${score * 20}%` }}></div>
            </div>
            <p className="text-[10px] text-frost-400">{levels[score] || 'Very Weak'}</p>
        </div>
    );
};

const AddSecretModal = ({ onSave, onClose }) => {
    const [form, setForm] = useState({ service: '', identity: '', password: '', notes: '' });
    const [saving, setSaving] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.service || !form.password) return;
        setSaving(true);
        await onSave(form);
        setSaving(false);
    };
    return (
        <div className="fixed inset-0 z-[200] bg-dark-700/80 backdrop-blur-xl flex items-center justify-center p-6">
            <div className="bg-dark-500 w-full max-w-lg rounded-2xl p-8 shadow-card-hover border border-white/[0.06] space-y-6">
                <h3 className="text-lg font-bold text-frost-900">Add Secret</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" placeholder="Service name (e.g., Gmail)" value={form.service} onChange={e => setForm({...form, service: e.target.value})} required
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    <input type="text" placeholder="Username / Email" value={form.identity} onChange={e => setForm({...form, identity: e.target.value})}
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    <div>
                        <input type="password" placeholder="Password / Secret" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required
                            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                        <PasswordStrength password={form.password} />
                    </div>
                    <textarea placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30 resize-none" />
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 py-3 border border-white/[0.06] text-frost-500 font-bold rounded-xl hover:bg-white/[0.03] text-sm">Cancel</button>
                        <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold rounded-xl shadow-glow text-sm">
                            {saving ? 'Encrypting...' : 'Save Secret'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PasswordVault;
